import { useEffect, useState } from "react";
import type { Table } from "@ai-pos/shared";
import { tablesApi } from "../api/resources.js";

interface FormState {
  id: string | null;
  name: string;
  isActive: boolean;
  sortOrder: number;
}

export function TableAdmin() {
  const [tables, setTables] = useState<Table[]>([]);
  const [form, setForm] = useState<FormState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const load = () => {
    tablesApi
      .list()
      .then((res) => setTables(res.tables))
      .catch((err: unknown) => setError(err instanceof Error ? err.message : "取得に失敗しました"));
  };

  useEffect(load, []);

  const handleSave = async () => {
    if (!form) return;
    setSaving(true);
    setError(null);
    try {
      if (form.id) {
        await tablesApi.update(form.id, { name: form.name, isActive: form.isActive, sortOrder: form.sortOrder });
      } else {
        await tablesApi.create({ name: form.name, isActive: form.isActive, sortOrder: form.sortOrder });
      }
      setForm(null);
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "保存に失敗しました");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (table: Table) => {
    if (!window.confirm(`「${table.name}」を削除します。よろしいですか？`)) return;
    setError(null);
    try {
      await tablesApi.remove(table.id);
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "削除に失敗しました");
    }
  };

  const handleMove = async (table: Table, direction: "up" | "down") => {
    const sorted = [...tables].sort((a, b) => a.sortOrder - b.sortOrder);
    const index = sorted.findIndex((t) => t.id === table.id);
    const neighborIndex = direction === "up" ? index - 1 : index + 1;
    const neighbor = sorted[neighborIndex];
    if (!neighbor) return;
    setError(null);
    try {
      await Promise.all([
        tablesApi.update(table.id, { sortOrder: neighbor.sortOrder }),
        tablesApi.update(neighbor.id, { sortOrder: table.sortOrder }),
      ]);
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "並び替えに失敗しました");
    }
  };

  const sortedTables = [...tables].sort((a, b) => a.sortOrder - b.sortOrder);

  return (
    <div className="stack">
      <div className="row">
        <h2 style={{ flex: 1 }}>テーブル管理</h2>
        <button
          className="btn btn-primary"
          onClick={() => setForm({ id: null, name: "", isActive: true, sortOrder: tables.length })}
        >
          新規追加
        </button>
      </div>
      {error && <div className="alert alert-error">{error}</div>}
      <div className="card">
        <table>
          <thead>
            <tr>
              <th>テーブル名</th>
              <th>状態</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {sortedTables.map((table, index) => (
              <tr key={table.id}>
                <td>{table.name}</td>
                <td>{table.isActive ? <span className="badge">有効</span> : <span className="badge badge-danger">停止</span>}</td>
                <td>
                  <div className="row">
                    <button className="btn" onClick={() => void handleMove(table, "up")} disabled={index === 0} aria-label="上へ">
                      ↑
                    </button>
                    <button
                      className="btn"
                      onClick={() => void handleMove(table, "down")}
                      disabled={index === sortedTables.length - 1}
                      aria-label="下へ"
                    >
                      ↓
                    </button>
                    <button
                      className="btn"
                      onClick={() => setForm({ id: table.id, name: table.name, isActive: table.isActive, sortOrder: table.sortOrder })}
                    >
                      編集
                    </button>
                    <button className="btn btn-danger btn-outline" onClick={() => void handleDelete(table)}>
                      削除
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {sortedTables.length === 0 && (
              <tr>
                <td colSpan={3} className="empty-state">
                  テーブルが登録されていません
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {form && (
        <div className="modal-backdrop" onClick={() => setForm(null)}>
          <div className="modal stack" onClick={(e) => e.stopPropagation()}>
            <h3>{form.id ? "テーブル編集" : "新規テーブル"}</h3>
            <div className="form-field">
              <label htmlFor="table-name">テーブル名</label>
              <input id="table-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <label className="row" style={{ gap: 6 }}>
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                style={{ minHeight: "auto", width: 20, height: 20 }}
              />
              有効
            </label>
            <div className="row">
              <button className="btn" onClick={() => setForm(null)}>
                キャンセル
              </button>
              <button className="btn btn-primary" onClick={() => void handleSave()} disabled={saving || !form.name}>
                保存
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
